import React from 'react';
import { removeMarkdown, extractLinks, normalizePhoneNumber } from '@/utils/textFormatting';

interface FormattedMessageProps {
  content: string;
}

interface ParsedContact {
  name: string;
  position?: string;
  email?: string;
  phone?: string;
  company?: string;
  city?: string;
}

// Detect if content is a contact list
function isContactList(content: string): boolean {
  const contactPattern = /•\s+[A-Za-zÀ-ú\s]+\([^)]+\)/g;
  const matches = content.match(contactPattern);
  return matches !== null && matches.length >= 2;
}

// Parse contacts from formatted text
function parseContacts(content: string): ParsedContact[] {
  const contacts: ParsedContact[] = [];
  
  // Split by empty lines (separator between contacts)
  const blocks = content.split(/\n\s*\n/);
  
  blocks.forEach(block => {
    const lines = block.trim().split('\n');
    const contact: ParsedContact = { name: '' };
    
    lines.forEach(line => {
      const cleanLine = line.replace(/^•\s*/, '').trim();
      
      // Name and position: pattern "Name (Position)"
      const nameMatch = cleanLine.match(/^([A-Za-zÀ-ú\s]+)\(([^)]+)\)/);
      if (nameMatch) {
        contact.name = nameMatch[1].trim();
        contact.position = nameMatch[2].trim();
        return;
      }
      
      // Email
      if (cleanLine.toLowerCase().includes('email:')) {
        contact.email = cleanLine.replace(/email:\s*/i, '').trim();
      }
      
      // Phone
      if (cleanLine.toLowerCase().includes('telefone:')) {
        contact.phone = cleanLine.replace(/telefone:\s*/i, '').trim();
      }
      
      // Company
      if (cleanLine.toLowerCase().includes('empresa:')) {
        contact.company = cleanLine.replace(/empresa:\s*/i, '').trim();
      }
      
      // City
      if (cleanLine.toLowerCase().includes('cidade:')) {
        contact.city = cleanLine.replace(/cidade:\s*/i, '').trim();
      }
    });
    
    if (contact.name) {
      contacts.push(contact);
    }
  });
  
  return contacts;
}

// Contact card component
function ContactCard({ contact, index }: { contact: ParsedContact; index: number }) {
  return (
    <div className="border border-border rounded-lg p-4 hover:bg-accent/50 transition-colors">
      {/* Header with number and name */}
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-sm">
          {index}
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-base">{contact.name}</h4>
          {contact.position && (
            <p className="text-sm text-muted-foreground">{contact.position}</p>
          )}
        </div>
      </div>
      
      {/* Contact information */}
      <div className="space-y-2 ml-11">
        {contact.email && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Email:</span>
            <a 
              href={`mailto:${contact.email}`}
              className="text-primary hover:underline font-medium"
            >
              {contact.email}
            </a>
          </div>
        )}
        
        {contact.phone && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Telefone:</span>
            {(() => {
              const phoneInfo = normalizePhoneNumber(contact.phone);
              if (phoneInfo.isMobile && phoneInfo.whatsappUrl) {
                return (
                  <a 
                    href={phoneInfo.whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:underline font-medium inline-flex items-center gap-1"
                    title="Abrir no WhatsApp"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    {contact.phone}
                  </a>
                );
              }
              return <span className="font-medium">{contact.phone}</span>;
            })()}
          </div>
        )}
        
        {contact.company && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Empresa:</span>
            <span>{contact.company}</span>
          </div>
        )}
        
        {contact.city && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Cidade:</span>
            <span>{contact.city}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function FormattedMessage({ content }: FormattedMessageProps) {
  // Remove markdown syntax
  const cleanContent = removeMarkdown(content);
  
  // Check if content is a contact list
  if (isContactList(cleanContent)) {
    const contacts = parseContacts(cleanContent);
    
    if (contacts.length > 0) {
      // Extract intro text (first line before contacts)
      const firstLine = cleanContent.split('\n')[0];
      const hasIntro = firstLine && !firstLine.includes('•');
      
      return (
        <div className="space-y-3">
          {/* Introductory text */}
          {hasIntro && (
            <p className="text-sm text-muted-foreground mb-4">
              {firstLine}
            </p>
          )}
          
          {/* Contact cards */}
          {contacts.map((contact, index) => (
            <ContactCard 
              key={index} 
              contact={contact} 
              index={index + 1} 
            />
          ))}
          
          {/* Closing message */}
          <p className="text-sm text-muted-foreground mt-4">
            Qual contato você gostaria de utilizar?
          </p>
        </div>
      );
    }
  }
  
  // Default formatting for regular messages
  const lines = cleanContent.split('\n');
  
  return (
    <div className="space-y-2">
      {lines.map((line, lineIndex) => {
        // Extract links from each line
        const parts = extractLinks(line);
        
        return (
          <p key={lineIndex} className="break-words">
            {parts.map((part, partIndex) => {
              if (part.type === 'email') {
                return (
                  <a
                    key={partIndex}
                    href={`mailto:${part.content}`}
                    className="text-primary hover:underline font-medium"
                  >
                    {part.content}
                  </a>
                );
              }
              
              if (part.type === 'url') {
                const href = part.content.startsWith('http') 
                  ? part.content 
                  : `https://${part.content}`;
                  
                return (
                  <a
                    key={partIndex}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline font-medium"
                  >
                    {part.content}
                  </a>
                );
              }
              
              if (part.type === 'phone' && part.isMobile && part.whatsappUrl) {
                return (
                  <a
                    key={partIndex}
                    href={part.whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:underline font-medium inline-flex items-center gap-1"
                    title="Abrir no WhatsApp"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    {part.content}
                  </a>
                );
              }
              
              return <span key={partIndex}>{part.content}</span>;
            })}
          </p>
        );
      })}
    </div>
  );
}
